<template>
  <v-card flat class="pa-6">
    <h2 class="text-h5 primary-header sans-serif text-center">
      {{ $t('form.import.upload.title') }}
    </h2>
    <v-card-subtitle class="text-center">
      {{ $t('form.import.upload.subtitle') }}
    </v-card-subtitle>
    <v-card-text>
      <v-file-input
        v-model="file"
        accept=".csv,.xlsx,.xls"
        :label="$t('form.import.upload.selectFile')"
        prepend-icon="mdi-file-upload"
        outlined
        show-size
        :loading="loading"
        @change="handleFileChange"
      />
      <p v-if="error" class="mt-4 warning--text">
        {{ error }}
      </p>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue'
import * as XLSX from 'xlsx'
import { BulkImportWizardParsedSheet } from '@betterboards/shared/types/Import'
import {
  ParseFileResult,
  isSheetHidden,
  hasIgnoreTag,
  detectHeaderRowTag,
  parseWorksheet
} from '@/helpers/import/xlsxParsing'

export default Vue.extend({
  name: 'BulkImportUpload',
  data: () => ({
    file: null as File | null,
    loading: false,
    error: ''
  }),
  methods: {
    async handleFileChange (file: File | null): Promise<void> {
      if (!file) {
        return
      }

      this.loading = true
      this.error = ''

      try {
        const result = await this.parseFile(file)
        this.$emit('fileLoaded', result)
      } catch (err) {
        console.error('Error parsing file:', err)
        this.error = this.$t('form.import.upload.parseError') as string
      } finally {
        this.loading = false
      }
    },
    async parseFile (file: File): Promise<ParseFileResult> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (e) => {
          try {
            const data = e.target?.result
            const workbook = XLSX.read(data, { type: 'array' })
            const detectedHeaderRows: Record<string, number> = {}

            // Filter out hidden sheets and sheets with $ignore/$skip tag
            const visibleSheetNames = workbook.SheetNames.filter(sheetName => {
              if (isSheetHidden(workbook, sheetName)) {
                return false
              }
              const worksheet = workbook.Sheets[sheetName]
              if (hasIgnoreTag(worksheet)) {
                return false
              }
              return true
            })

            const sheets: BulkImportWizardParsedSheet[] = visibleSheetNames.map(sheetName => {
              const worksheet = workbook.Sheets[sheetName]
              const headerRow = detectHeaderRowTag(worksheet)
              detectedHeaderRows[sheetName] = headerRow
              return parseWorksheet(workbook, sheetName, headerRow)
            })

            resolve({ sheets, workbook, detectedHeaderRows })
          } catch (err) {
            reject(err)
          }
        }

        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsArrayBuffer(file)
      })
    }
  }
})
</script>
